import {
  Dispatch,
  FC,
  ReactNode,
  createContext,
  useContext,
  useReducer,
} from 'react';

type TStudentFiltersState = {
  grade: string;
  section: string;
};

enum ACTION_TYPE {
  UPDATE_FILTERS = 'UPDATE_FILTERS',
}

type Action = {
  type: ACTION_TYPE.UPDATE_FILTERS;
  payload: Partial<TStudentFiltersState>;
};

const initialStudentFiltersState: TStudentFiltersState = {
  grade: 'select',
  section: 'select',
};

const StudentFiltersContext = createContext<TStudentFiltersState | undefined>(
  undefined
);
const StudentFiltersDispatchContext = createContext<
  Dispatch<Action> | undefined
>(undefined);

const StudentFiltersReducer = (
  state: TStudentFiltersState,
  action: Action
): TStudentFiltersState => {
  switch (action.type) {
    case ACTION_TYPE.UPDATE_FILTERS:
      return {
        ...state,
        ...action.payload,
      };
    default:
      throw new Error('Unhandled action type');
  }
};

export const StudentFiltersProvider: FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [state, dispatch] = useReducer(
    StudentFiltersReducer,
    initialStudentFiltersState
  );

  return (
    <StudentFiltersContext.Provider value={state}>
      <StudentFiltersDispatchContext.Provider value={dispatch}>
        {children}
      </StudentFiltersDispatchContext.Provider>
    </StudentFiltersContext.Provider>
  );
};

export const useStudentFiltersState = () => {
  const state = useContext(StudentFiltersContext);
  if (state === undefined) {
    throw new Error(
      'useStudentFiltersState must be used within a StudentFiltersProvider'
    );
  }
  return state;
};

export const useStudentFiltersActions = () => {
  const dispatch = useContext(StudentFiltersDispatchContext);
  if (!dispatch) {
    throw new Error(
      'useStudentFiltersActions dispatch must be used within a StudentFiltersProvider'
    );
  }

  const updateFilters = (filters: Partial<TStudentFiltersState>) => {
    dispatch({ type: ACTION_TYPE.UPDATE_FILTERS, payload: filters });
  };

  return { updateFilters };
};
